import React from 'react';
import styled from 'styled-components';
import {
    Button,
    Typography,
    VerticalSpacer,
    SectionInnerGridContent,
    GridContainer,
    EyebrowProps,
    ScrollIndicator
} from '../../elements';
import {AnimateOnViewWrap} from '../../wraps';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';

const Styled = styled.div`
    position: relative;
    color: ${(p) => p.theme.palette.text.primary};

    padding-top: ${(p) => p.theme.spacing(16)}px;
    ${(p) => p.theme.breakpoints.down('sm')} {
        padding-top: ${(p) => p.theme.spacing(12)}px;
    }
    width: 100%;

    .buttons-container {
        display: flex;
        justify-content: center;
        flex-flow: row nowrap;
        ${(p) => p.theme.breakpoints.down('sm')} {
            flex-flow: row wrap;
        }
        & > span {
            padding-left: ${(p) => p.theme.spacing(2)}px;
            padding-right: ${(p) => p.theme.spacing(2)}px;
            ${(p) => p.theme.breakpoints.down('sm')} {
                padding-top: ${(p) => p.theme.spacing(2)}px;
            }
        }
    }

    .h3, .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
    }

    .caption {
        font-size: 10px;
        line-height: 16px;
        letter-spacing: 0.2px;
    }
`;

export interface SimpleHeaderProps {
    accentColor?: AccentColorOptions;
    eyebrow?: EyebrowProps;
    title?: string;
    description?: string;
    caption?: string;
    buttonText?: string;
    href?: string;
    secondaryButtonText?: string;
    secondaryHref?: string;
    minAnimateDelay?: number;
    showScrollIndicator?: boolean;
}

export const SimpleHeader = ({
    accentColor,
    eyebrow,
    title,
    description,
    caption,
    buttonText,
    href,
    secondaryButtonText,
    secondaryHref,
    minAnimateDelay = 0.75,
    showScrollIndicator = false
}: SimpleHeaderProps): JSX.Element | null => {
    const isSm = useBreakpointDown('sm');
    return (
        <Styled className={'ue-simple-header'}>
            <GridContainer>
                <SectionInnerGridContent maxWidth={528}>
                    {eyebrow && eyebrow.text && (
                        <>
                            <AnimateOnViewWrap delay={minAnimateDelay} translateY={-20}>
                                <Typography variant="h4" big>
                                    {eyebrow.text}
                                </Typography>
                            </AnimateOnViewWrap>
                        </>
                    )}
                    {title && (
                        <>
                            <VerticalSpacer variant="heading" />
                            <AnimateOnViewWrap delay={minAnimateDelay + 0.1} translateY={-15}>
                                <Typography variant="h1" petit>{title}</Typography>
                            </AnimateOnViewWrap>
                        </>
                    )}
                    {description && (
                        <>
                            <VerticalSpacer variant="heading" />
                            <AnimateOnViewWrap delay={minAnimateDelay + 0.2} translateY={-10}>
                                <Typography variant="h3">{description}</Typography>
                            </AnimateOnViewWrap>
                        </>
                    )}
                    {caption && (
                        <>
                            <VerticalSpacer variant="heading" />
                            <AnimateOnViewWrap delay={minAnimateDelay + 0.25} translateY={-10}>
                                <Typography variant="p" caption>
                                    {caption}
                                </Typography>
                            </AnimateOnViewWrap>
                        </>
                    )}
                    {(href && buttonText) || (secondaryHref && secondaryButtonText) ? (
                        <>
                            <VerticalSpacer variant="heading" />
                            <AnimateOnViewWrap delay={minAnimateDelay + 0.25} translateY={-10}>
                                <div className="buttons-container">
                                    {href && buttonText ? (
                                        <Button type="primary" href={href} color={accentColor}>
                                            {buttonText ? buttonText : 'Learn more'}
                                        </Button>
                                    ) : (
                                        ''
                                    )}
                                    {secondaryHref && secondaryButtonText ? (
                                        <Button
                                            type="secondary"
                                            href={secondaryHref}
                                            color={accentColor}>
                                            {secondaryButtonText}
                                        </Button>
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </AnimateOnViewWrap>
                        </>
                    ) : (
                        ''
                    )}
                    <VerticalSpacer variant="custom" spacing={6.5}/>
                    {showScrollIndicator && !isSm && <ScrollIndicator />}
                </SectionInnerGridContent>
            </GridContainer>
        </Styled>
    );
};
